import { PerformanceSDKOptions, PerformanceMetrics } from '../types';
import { getPerformanceMetrics, observePerformanceMetrics } from '../utils/performance';
import { MemoryLeakDetector } from '../utils/memory-leak';
import { PerformanceReporter } from '../utils/reporter';
import { PowerMonitor } from '../utils/power-monitor';

/**
 * 前端性能监控SDK
 * 支持监控FCP、LCP、TBT、TTI和内存泄漏
 */
export class PerformanceSDK {
  private options: PerformanceSDKOptions;
  private memoryLeakDetector: MemoryLeakDetector;
  private reporter: PerformanceReporter;
  private powerMonitor: PowerMonitor;
  private isStarted = false;
  private observers: PerformanceObserver[] = [];

  constructor(options: PerformanceSDKOptions = {}) {
    this.options = {
      autoStart: true,
      enableMemoryMonitoring: true,
      enableWebVitals: true,
      enablePowerMonitoring: true,
      reportInterval: 30000,
      ...options
    };

    // 初始化内存泄漏检测器
    this.memoryLeakDetector = new MemoryLeakDetector((leakInfo) => {
      console.warn('Memory leak detected:', leakInfo);
      this.handleMemoryLeak(leakInfo);
    });

    // 初始化数据上报器
    this.reporter = new PerformanceReporter(
      this.options.reportUrl,
      this.options.reportInterval
    );

    // 初始化能耗监控器
    this.powerMonitor = new PowerMonitor((powerInfo) => {
      console.log('Power consumption info:', powerInfo);
      this.handlePowerChange(powerInfo);
    });

    // 如果配置了自动开始，则启动监控
    if (this.options.autoStart) {
      this.start();
    }
  }

  /**
   * 开始性能监控
   */
  start(): void {
    if (this.isStarted) {
      console.warn('PerformanceSDK is already started');
      return;
    }

    this.isStarted = true;
    console.log('PerformanceSDK started');

    // 启动数据上报
    this.reporter.start();

    // 启动内存泄漏检测
    if (this.options.enableMemoryMonitoring) {
      this.memoryLeakDetector.start();
    }

    // 启动能耗监控
    if (this.options.enablePowerMonitoring) {
      this.powerMonitor.start();
    }

    // 设置性能指标监听
    if (this.options.enableWebVitals) {
      this.setupPerformanceObservers();
    }

    // 页面加载完成后获取初始指标
    if (document.readyState === 'complete') {
      this.collectAndReport();
    } else {
      window.addEventListener('load', () => {
        this.collectAndReport();
      });
    }
  }

  /**
   * 停止性能监控
   */
  stop(): void {
    if (!this.isStarted) {
      console.warn('PerformanceSDK is not started');
      return;
    }

    this.isStarted = false;
    console.log('PerformanceSDK stopped');

    // 停止数据上报
    this.reporter.stop();

    // 停止内存泄漏检测
    this.memoryLeakDetector.stop();

    // 停止能耗监控
    this.powerMonitor.stop();

    // 清理观察器
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }

  /**
   * 获取当前性能指标
   */
  getMetrics(): PerformanceMetrics {
    return getPerformanceMetrics();
  }

  /**
   * 手动收集并上报数据
   */
  collectAndReport(): void {
    const metrics = this.getMetrics();
    
    // 调用自定义数据处理函数
    if (this.options.onData) {
      this.options.onData(metrics);
    }

    // 添加到上报队列
    this.reporter.addData(metrics);
  }

  /**
   * 设置性能指标观察器
   */
  private setupPerformanceObservers(): void {
    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported');
      return;
    }

    try {
      // 监听LCP
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          this.collectAndReport();
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      this.observers.push(lcpObserver);

      // 监听FCP
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          this.collectAndReport();
        }
      });
      fcpObserver.observe({ entryTypes: ['paint'] });
      this.observers.push(fcpObserver);

      // 监听长任务
      const longTaskObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          this.collectAndReport();
        }
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
      this.observers.push(longTaskObserver);

    } catch (error) {
      console.warn('Failed to setup performance observers:', error);
    }
  }

  /**
   * 处理内存泄漏事件
   */
  private handleMemoryLeak(leakInfo: { current: any; trend: number }): void {
    const metrics = this.getMetrics();
    metrics.memory = leakInfo.current;
    
    // 添加内存泄漏标记
    const leakData = {
      ...metrics,
      memoryLeak: {
        detected: true,
        trend: leakInfo.trend,
        timestamp: Date.now()
      }
    };

    // 调用自定义数据处理函数
    if (this.options.onData) {
      this.options.onData(leakData as PerformanceMetrics);
    }

    // 添加到上报队列
    this.reporter.addData(leakData as PerformanceMetrics);
  }

  /**
   * 处理能耗变化事件
   */
  private handlePowerChange(powerInfo: any): void {
    const metrics = this.getMetrics();
    metrics.powerConsumption = powerInfo;

    // 调用自定义数据处理函数
    if (this.options.onData) {
      this.options.onData(metrics);
    }

    // 添加到上报队列
    this.reporter.addData(metrics);
  }

  /**
   * 获取内存历史记录
   */
  getMemoryHistory(): any[] {
    return this.memoryLeakDetector.getMemoryHistory();
  }

  /**
   * 获取上报队列大小
   */
  getQueueSize(): number {
    return this.reporter.getQueueSize();
  }

  /**
   * 立即上报数据
   */
  async reportNow(): Promise<void> {
    await this.reporter.report();
  }

  /**
   * 检查是否已启动
   */
  isRunning(): boolean {
    return this.isStarted;
  }

  /**
   * 销毁SDK实例
   */
  destroy(): void {
    this.stop();
    this.memoryLeakDetector.clearHistory();
    this.reporter.clearQueue();
  }
} 